/* eslint-disable @typescript-eslint/no-unused-vars */
import { useMediaQuery } from "@mui/material";
import { useImagePath, useAppDispatch } from "hooks";
import { signUpIncentiveAssets } from "assets/cdn";
import {
	Body,
	Container,
	RewardItem,
	ItemImage,
	RewardAmountContainer,
	RewardAmount,
	Box,
	Items,
	Title,
	SubTitle,
	Footer,
	Button,
	ButtonContainer
} from "./styles";
import useCalculateScale from "components/PopUpManager/hooks/useCalculateScale";
import CloseIconButton from "components/AppModalDialogsV2/Dialogs/lib/CloseIconButton";
import {
	onClosePopUp,
	onScrollToShopSection,
	setRewardsPopupFlag
} from "redux/slices";
import SectionButton from "components/Buttons/SectionButton";
import { useSections } from "pages/LandingPage/sections";
import { featureTrackerAPI } from "redux/apis";
import { FeatureType } from "interfaces/feature-tracker";
import { useGoogleAnalyticsContext, useWebStoreContext } from "contexts";
import useRewardsById from "hooks/useRewardsById";
import { SignUpIncentiveResponse } from "interfaces/sign-up-incentive";
import { useNavigate } from "react-router-dom";
import { useLinks } from "hooks/useLinks";
import { sortWithSpecificId } from "utils/sort";

interface SignUpIncentiveRewardProps {}

export const SignUpIncentiveReward: React.FC<
	SignUpIncentiveRewardProps
> = () => {
	const { getImage } = useImagePath("cdn");
	const links = useLinks();
	const isPortrait = useMediaQuery("(orientation: portrait)");
	const headImageUrl = getImage(
		isPortrait
			? signUpIncentiveAssets.backgroundMobile
			: signUpIncentiveAssets.background
	);
	const { authToken } = useWebStoreContext();
	const { data }: { data?: SignUpIncentiveResponse } =
		featureTrackerAPI.endpoints.getFeatureTracker.useQueryState({
			FeatureType: FeatureType.SIGN_UP_INCENTIVE,
			token: authToken?.value
		});
	const navigate = useNavigate();

	const displayRewards = useRewardsById(
		data?.tracker?.incentiveRewardId as string
	);
	const scale = useCalculateScale(
		isPortrait ? 400 + 40 : 1002 + 40,
		isPortrait ? 714 + 40 : 430 + 40
	);
	const isClaimed = !!data?.tracker?.scopelyUserId;
	const dispatch = useAppDispatch();
	const { reportEvent, reportPlaygamiEvent } = useGoogleAnalyticsContext();

	const onClose = () => {
		reportEvent("scopely_sign_up_incentive", {
			source: "popup",
			action: "close",
			popup_type: isClaimed ? "reward" : "announcement"
		});
		reportPlaygamiEvent("tp.web_frontend", {
			event_name: "scopely_sign_up_incentive",
			event_type: "web_scopely_sign-up_incentive",
			action: "close",
			source: "popup",
			type: isClaimed ? "reward" : "announcement"
		});
		dispatch(onClosePopUp());
		isClaimed && dispatch(setRewardsPopupFlag(true));
	};

	const scopelyInfo = useSections().find(
		(section) => section.title === links.sign_up_scopely.title
	);

	const orderedRewards = sortWithSpecificId(
		displayRewards,
		"loyalty_points",
		"last"
	);

	if (!data?.tracker?.incentiveRewardId) {
		return null;
	}

	return (
		<Container scale={scale} backgroundImage={headImageUrl}>
			<CloseIconButton onClose={onClose} />
			<Body className={isClaimed ? "claimed" : ""}>
				{isClaimed ? (
					<Title className="claimed">
						Your Scopely sign-up reward is ready!
					</Title>
				) : (
					<Title>
						<div>Unlock FREE</div>
						<div>
							{Number(orderedRewards?.[0]?.description.replace(",", "")) > 1
								? orderedRewards?.[0]?.pluralLabel
								: orderedRewards?.[0]?.singularLabel}
						</div>
					</Title>
				)}

				{isClaimed ? (
					<SubTitle>
						Your Loyalty Points are in your balance, and the rest of your reward
						is waiting in your in-game inbox.
					</SubTitle>
				) : (
					<SubTitle>
						Sign up for Scopely account to stay updated with the latest offers,
						exclusive deals and get:
					</SubTitle>
				)}
				<Items>
					{orderedRewards?.map((displayReward) => (
						<Box key={displayReward.id}>
							<RewardItem>
								<ItemImage
									src={
										displayReward?.imageUrl || displayReward?.overrideImageUrl
									}
									alt=""
								/>
								<RewardAmountContainer>
									<RewardAmount style={{ paintOrder: "stroke fill" }}>
										{displayReward.description}
									</RewardAmount>
								</RewardAmountContainer>
							</RewardItem>
						</Box>
					))}
				</Items>
				<ButtonContainer className="claimed">
					{isClaimed ? (
						<Button
							variant="contained"
							themeColor="secondary"
							sx={{
								maxWidth: isPortrait ? "90%" : "65%"
							}}
							onClick={() => {
								reportEvent("scopely_sign_up_incentive", {
									source: "popup",
									action: "click",
									popup_type: "reward"
								});
								reportPlaygamiEvent("tp.web_frontend", {
									event_name: "scopely_sign_up_incentive",
									event_type: "web_scopely_sign-up_incentive",
									action: "click",
									source: "popup",
									type: "reward"
								});
								navigate(links.shop.path);
								dispatch(onScrollToShopSection("Featured Deals"));
								onClose();
							}}
						>
							Continue Shopping
						</Button>
					) : (
						<SectionButton
							href={scopelyInfo?.linkHref || ""}
							title={scopelyInfo?.title || ""}
							text={scopelyInfo?.linkText || ""}
							analytics={"scopely_sign_up_incentive#popup#announcement"}
							playgamiAnalytics="web_scopely_sign_up_incentive#popup#announcement"
							isExternal={scopelyInfo?.linkIsExternal || false}
							openExternalSameTab={scopelyInfo?.openExternalSameTab || false}
						/>
					)}
				</ButtonContainer>
				{isClaimed ? <></> : <Footer>Sync your progress across devices</Footer>}
			</Body>
		</Container>
	);
};
